import React, { useEffect, useState } from 'react';
import { shape, string } from 'prop-types';
import { Button, Empty } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@cards/Card';
import {
  whatTypes,
  whatLabelTypes,
  whoTypes,
  whenTypes,
  DELAY,
  GROUP,
  INDEX_EVENT,
  INDEX_EVENT_DELAY,
} from '@constants';
import { contentTypesList } from '@utils/contentTypes';
import { selectRule } from '@pathways/redux/rules/reducers';
import { fetchRules } from '@pathways/redux/rules/actions';
import {
  getContentAudienceType,
  getContentTitle,
  useRuleContentRefs,
} from '@pathways/hooks/useRuleContentRefs';
import DetailForm from '@components/DetailForm';
import DetailRow from '@components/DetailRow';
// import LanguageIcon from '@components/LanguageIcon';
import None from '@components/None';
import ContentPreview from './ContentPreview';
import './style.less';

const isNewOrEditTest = pathname => !pathname.includes('/new') && !pathname.includes('/edit');

const WHAT = 'what';
const WHEN = 'when';

function ProcedureRuleDetails({ ruleData, ruleId, title, ...props }) {
  const [rule, setRule] = useState(null);
  const [isPreviewVisible, setPreviewVisible] = useState(false);
  const [previewType, setPreviewType] = useState(WHAT);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [loading, storedRuleData] = useSelector(selectRule(ruleId));
  const { rules: hydratedRules } = useRuleContentRefs(rule ? [rule] : []);
  const displayRule = hydratedRules[0] || rule;

  useEffect(() => {
    dispatch(fetchRules());
  }, [dispatch]);

  useEffect(() => {
    if (ruleData) {
      setRule(ruleData);
    } else if (storedRuleData) {
      setRule(storedRuleData);
    }
  }, [storedRuleData, ruleData]);

  const whatDetailTitle = getContentTitle(displayRule?.whatDetail);
  const audienceType =
    displayRule?.audienceType || getContentAudienceType(displayRule?.whatDetail);

  return (
    <>
      <Card.Half
        className="procedure-rule-details"
        loading={isNewOrEditTest(history.location.pathname) && loading}
        title={title}
        {...props}
      >
        {displayRule ? (
          <DetailForm>
            <h3>{t('cards:ProcedureRuleDetails.title')}</h3>

            <DetailRow label={t('cards:ProcedureRuleDetails.name')} value={displayRule.name} />
            <DetailRow
              label={t('cards:ProcedureRuleDetails.description')}
              value={displayRule.description || <None />}
            />
            {/* <DetailRow
              label={t('cards:ProcedureRuleDetails.language')}
              value={<LanguageIcon language={rule.language || rule.metadata.language} showText />}
            /> */}
            <h3>{t('cards:ProcedureRuleDetails.what.title')}</h3>
            <DetailRow
              label={t('cards:ProcedureRuleDetails.what.action')}
              value={t(whatTypes[displayRule.what])}
            />
            {displayRule.whatDetail && whatDetailTitle ? (
              <>
                <DetailRow
                  label={t(whatLabelTypes[displayRule.what])}
                  value={whatDetailTitle}
                />
                <DetailRow
                  label=" "
                  value={
                    <Button
                      onClick={() => {
                        setPreviewType(WHAT);
                        setPreviewVisible(true);
                      }}
                    >
                      {t('common:buttons.preview')}
                    </Button>
                  }
                />
                {audienceType && (
                  <DetailRow
                    className="what-audience"
                    label={`${t(whatLabelTypes[displayRule.what])} ${t(
                      'cards:ProcedureRuleDetails.what.audience.title',
                    )}`}
                    value={t(`cards:ProcedureRuleDetails.what.audience.${audienceType}`)}
                  />
                )}
              </>
            ) : null}
            <h3>{t('cards:ProcedureRuleDetails.who.title')}</h3>
            <DetailRow
              label={t('cards:ProcedureRuleDetails.who.audience')}
              value={
                displayRule.who === GROUP ? (
                  <>
                    <p>
                      <strong>{t(whoTypes[displayRule.who])}</strong>
                    </p>
                    {displayRule.whoDetail.map(detail => (
                      <p key={detail.id}>{detail.name}</p>
                    ))}
                  </>
                ) : (
                  t(whoTypes[displayRule.who])
                )
              }
            />
            <h3>{t('cards:ProcedureRuleDetails.when.title')}</h3>
            <DetailRow
              label={t('cards:ProcedureRuleDetails.when.title')}
              value={
                <>
                  {displayRule.when === DELAY
                    ? `${displayRule.whenDetail.days} ${t(whenTypes[displayRule.when])}`
                    : null}
                  {(displayRule.when === INDEX_EVENT || displayRule.when === INDEX_EVENT_DELAY) &&
                  displayRule.whenDetail?.days
                    ? t(whenTypes[INDEX_EVENT_DELAY], {
                        context: Number(displayRule.whenDetail.days) > 0 ? '' : 'negative',
                        days: Math.abs(Number(displayRule.whenDetail.days)),
                      })
                    : null}
                  {displayRule.when !== DELAY &&
                  displayRule.when !== INDEX_EVENT_DELAY &&
                  !(displayRule.when === INDEX_EVENT && displayRule.whenDetail?.days)
                    ? t(whenTypes[displayRule.when])
                    : null}
                </>
              }
            />
            {displayRule.whenDetail?.name &&
            (contentTypesList.includes(displayRule.whenDetail?.type) ||
              contentTypesList.includes(displayRule.whenDetail?.metadata?.type)) ? (
              <>
                <DetailRow
                  label={t('common:Menu.content.title')}
                  value={displayRule.whenDetail.name}
                />
                <DetailRow
                  label=" "
                  value={
                    <Button
                      onClick={() => {
                        setPreviewType(WHEN);
                        setPreviewVisible(true);
                      }}
                    >
                      {t('common:buttons.preview')}
                    </Button>
                  }
                />
              </>
            ) : null}
          </DetailForm>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card.Half>
      {displayRule?.[`${previewType}Detail`]?.content && (
        <ContentPreview
          previewType={previewType}
          rule={displayRule}
          visible={isPreviewVisible}
          onCancel={() => setPreviewVisible(false)}
        />
      )}
    </>
  );
}

ProcedureRuleDetails.propTypes = {
  ruleData: shape({}),
  ruleId: string,
};

ProcedureRuleDetails.defaultProps = {
  ruleData: null,
  ruleId: null,
};

export default ProcedureRuleDetails;
